import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth, storage, db } from "../firebase";
import { ref, listAll, getDownloadURL, uploadBytes } from "firebase/storage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { logDownload } from "../lib/downloads";
import { fetchLikedPaths, setLiked } from "../lib/likes";
import { daysLeft, fetchRetention, toDate } from "../lib/retention";
import { albumUnlocked, fetchAccess } from "../lib/galleryAccess";
import { downloadAsZip } from "../lib/zipDownload";
import { albumOf } from "../lib/albums";
import { batches, fetchAsFiles, shareBatch, shouldOfferShare } from "../lib/shareFiles";
import "../style.css";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "lensdance29@gmail.com";

/* ---------------------------------------------
 * iOS video priming
 * ---------------------------------------------- */
let __videosPrimed = false;
function isLikelyIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isiOSDevice =
    /iP(hone|od|ad)/.test(ua) ||
    (/Mac/.test(ua) && typeof window !== "undefined" && "ontouchend" in window);
  return isiOSDevice;
}
function primeIOSVideosOnce() {
  if (__videosPrimed || !isLikelyIOS()) return;
  __videosPrimed = true;
  const vids = document.querySelectorAll('video[data-prime="1"]');
  vids.forEach((v) => {
    try {
      v.muted = true;
      v.playsInline = true;
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => v.pause()).catch(() => {});
      } else {
        v.pause();
      }
    } catch {}
  });
}
if (typeof window !== "undefined") {
  const handler = () => {
    primeIOSVideosOnce();
    window.removeEventListener("touchstart", handler, true);
    window.removeEventListener("pointerdown", handler, true);
    window.removeEventListener("click", handler, true);
  };
  window.addEventListener("touchstart", handler, true);
  window.addEventListener("pointerdown", handler, true);
  window.addEventListener("click", handler, true);
}

/* ---------------------------------------------
 * Downloads helpers
 * ---------------------------------------------- */
function buildAttachmentURL(url, filename) {
  try {
    const u = new URL(url);
    u.searchParams.set("alt", "media");
    u.searchParams.set(
      "response-content-disposition",
      `attachment; filename="${(filename || "download").replace(/"/g, "")}"`
    );
    u.searchParams.set("response-content-type", "application/octet-stream");
    return u.toString();
  } catch {
    return url;
  }
}
async function nativeDownload(url, filename = "download", opts = { prefer: "auto" }) {
  const dlUrl = buildAttachmentURL(url, filename);
  const blobDownload = async () => {
    const res = await fetch(dlUrl, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.setAttribute("download", filename || "download");
      a.style.position = "fixed";
      a.style.left = "-9999px";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };
  if (opts?.prefer === "blob") return blobDownload();
  if (!opts || opts.prefer === "auto") {
    const ext = (url.split("?")[0].split(".").pop() || "").toLowerCase();
    const isVideo = /(mp4|mov|avi|mkv|webm)$/i.test(ext);
    if (isVideo) {
      try {
        let frame = document.getElementById("hidden-download-frame");
        if (!frame) {
          frame = document.createElement("iframe");
          frame.id = "hidden-download-frame";
          frame.style.display = "none";
          document.body.appendChild(frame);
        }
        frame.src = dlUrl;
        return;
      } catch {}
    } else {
      try {
        return await blobDownload();
      } catch {}
    }
  }
  if (opts?.prefer === "iframe") {
    try {
      let frame = document.getElementById("hidden-download-frame");
      if (!frame) {
        frame = document.createElement("iframe");
        frame.id = "hidden-download-frame";
        frame.style.display = "none";
        document.body.appendChild(frame);
      }
      frame.src = dlUrl;
      return;
    } catch {}
  }
  try {
    const a = document.createElement("a");
    a.href = dlUrl;
    a.setAttribute("download", filename || "download");
    a.rel = "noopener";
    a.style.position = "fixed";
    a.style.left = "-9999px";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  } catch {
    window.location.href = dlUrl;
  }
}

/* ---------------------------------------------
 * Media utils
 * ---------------------------------------------- */
const extFromUrl = (url = "") => url.split("?")[0].split(".").pop().toLowerCase();
const isVideoExt = (ext = "") => /(mp4|mov|avi|mkv|webm)$/i.test(ext || "");
const isImageExt = (ext = "") => /(png|jpg|jpeg|gif|webp|heic|heif|svg)$/i.test(ext || "");
const isVideoUrl = (url = "") => isVideoExt(extFromUrl(url));


/* ---------------------------------------------
 * MediaTile – כל תמונות הדף נטענות מיד
 * ---------------------------------------------- */
// eslint-disable-next-line no-unused-vars
const MediaTile = React.memo(({ url, alt, isVideo, onClick, variant = "half", idx }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`tile ${variant}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      style={{ position: "relative", overflow: "hidden", cursor: "pointer" }}
    >
      {isVideo ? (
        /* סרטון בגריד: רק placeholder + אייקון play – לא טוענים את הסרטון */
        <>
          <div style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            background: "#1a1a1a",
          }} />
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: 64, height: 64, borderRadius: "50%",
            background: "rgba(255,255,255,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
            pointerEvents: "none",
          }}>
            <svg viewBox="0 0 24 24" width="32" height="32" fill="#333">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
          <div style={{
            position: "absolute", bottom: 8, left: 0, right: 0,
            textAlign: "center", color: "#fff", fontSize: "0.75rem",
            textShadow: "0 1px 3px rgba(0,0,0,0.8)",
            pointerEvents: "none",
          }}>
            {t("me.playVideo")}
          </div>
        </>
      ) : (
        <img
          src={url}
          alt={alt}
          className="tile-media"
          loading={idx < 4 ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={idx < 3 ? "high" : "auto"}
          style={{
            position: "absolute",
            top: 0, left: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
});

/* ---------------------------------------------
 * listAllRecursive – גם תתי־תיקיות
 * ---------------------------------------------- */
async function listAllRecursive(folderRef) {
  const res = await listAll(folderRef);
  const files = [...res.items];

  for (const prefix of res.prefixes) {
    const childFiles = await listAllRecursive(prefix);
    files.push(...childFiles);
  }

  return files;
}

/* ---------------------------------------------
 *   תיקיות אפשריות למשתמש (לשילוב גרסאות ישנות/חדשות)
 * ---------------------------------------------- */
function getUserFolderCandidates(user) {
  const email = user.email || "";
  const uid = user.uid || "";

  const sanitized = email.replace(/[.#$[\]]/g, "_");
  const lowerSanitized = email.toLowerCase().replace(/[.#$[\]]/g, "_");
  const plain = email;
  const lowerPlain = email.toLowerCase();

  const set = new Set(
    [sanitized, lowerSanitized, plain, lowerPlain, uid].filter(Boolean)
  );

  return Array.from(set);
}

/* ---------------------------------------------
 * Page
 * ---------------------------------------------- */
export default function MePage() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [installingAll, setInstallingAll] = useState(false);
  /** { done, total, phase } while a zip is being built; null otherwise. */
  const [zipProgress, setZipProgress] = useState(null);
  /* Shown in the page rather than in alert(). A native alert blocks the whole
     renderer until somebody presses OK — including the download that raised
     it — and on a phone it lands as a system dialog over a half-finished
     save. This is a note, not an interruption. */
  const [downloadNote, setDownloadNote] = useState("");
  /* Photos fetched and waiting for the share sheet, in sheet-sized batches.
     They sit here between the two taps: iOS will not open a share sheet from
     a gesture that has already been spent on twenty downloads, so fetching
     and sharing are deliberately separate actions. */
  const [shareQueue, setShareQueue] = useState(null); // { groups, index } | null
  const [error, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [filter, setFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // New state variables for cover layout
  const [userData, setUserData] = useState(null);
  const [activeHorse, setActiveHorse] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loaded, setLoaded] = useState({}); // fade-in tracking, keyed by item id

  // Whose gallery we're showing. The admin can open any client's page via
  // /me?uid=<uid>; everyone else always sees their own.
  const [searchParams] = useSearchParams();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const requestedUid = searchParams.get("uid");
  const targetUid = isAdmin && requestedUid ? requestedUid : user?.uid || null;

  /* Favourites.
     Held as a Set of Storage paths so rendering a tile is one O(1) lookup
     rather than a scan. Keyed on targetUid, not the viewer: when Alina opens a
     client's gallery through /me?uid=… she sees THEIR hearts, which is the
     whole point of the feature. She cannot set them — only the owner can, see
     `canLike` below and the matching rule in firestore.rules. */
  const [likedPaths, setLikedPaths] = useState(() => new Set());
  const canLike = !!user?.uid && user.uid === targetUid;

  /* ── RELEASED, OR STILL A PREVIEW? ──────────────────────────────────────
     A gallery goes up before the balance is paid, so until Alina releases it
     the client gets a preview: no downloads, and the pictures themselves
     shown small, soft and under her logo.

     Why degrade the picture instead of "protecting the screen": a website
     cannot stop a screenshot. WhatsApp and Instagram can, because they are
     installed apps and can ask the operating system for it (FLAG_SECURE on
     Android); on their own websites screenshots work perfectly well. Since
     the capture cannot be prevented, what gets captured is made worthless
     instead — a screenshot of a locked gallery is a small watermarked image,
     which is exactly what a download would have been.

     Alina always sees the real thing: she is not the one being asked to pay.
     Starts as `null` (unknown) rather than false, so the first paint does not
     flash a "locked" notice at a client whose gallery is in fact open. */
  const [access, setAccess] = useState(null);

  useEffect(() => {
    if (!targetUid) { setAccess(null); return undefined; }
    let alive = true;
    fetchAccess(targetUid).then((v) => { if (alive) setAccess(v); });
    return () => { alive = false; };
  }, [targetUid]);

  /* Locked is now a question about a photo, not about a person: a rider who
     settled one competition gets that one, and the weekend they have not paid
     for stays a preview. Alina always sees everything — she is not the one
     being asked to pay. `null` means not read yet, and nothing is treated as
     locked until it has been, so an open gallery never flashes shut. */
  const isLocked = React.useCallback(
    (album) => !isAdmin && access !== null && !albumUnlocked(access, album || ""),
    [isAdmin, access]
  );

  useEffect(() => {
    if (!targetUid) { setLikedPaths(new Set()); return undefined; }
    let alive = true;
    fetchLikedPaths(targetUid)
      .then((s) => { if (alive) setLikedPaths(s); })
      .catch((e) => console.warn("Could not load likes:", e?.code || e));
    return () => { alive = false; };
  }, [targetUid]);

  /**
   * Flip a heart. The UI moves first and the write follows, because a like is
   * a low-stakes preference and waiting on a round-trip makes it feel broken.
   * A failed write puts the heart back where it was.
   */
  const handleToggleLike = async (item) => {
    if (!canLike) return;
    const path = item.id;
    const next = !likedPaths.has(path);

    setLikedPaths((prev) => {
      const s = new Set(prev);
      if (next) s.add(path); else s.delete(path);
      return s;
    });

    try {
      await setLiked({ uid: user.uid, email: user.email, path, name: item.name, next });
    } catch (e) {
      console.warn("Could not save like:", e?.code || e);
      setLikedPaths((prev) => {
        const s = new Set(prev);
        if (next) s.delete(path); else s.add(path);
        return s;
      });
    }
  };

  /* ── The cover image, in three states ──────────────────────────────────
     1. A cover somebody chose        → always that one. `coverImage` is only
                                        ever written by an explicit upload, so
                                        its presence means a human picked it
                                        and nothing here may override it.
     2. No chosen cover, photos exist → borrow one of the photos.
     3. No chosen cover, no photos    → no picture at all.

     Case 3 is the point of this: the old fallback was a stock image from
     /pics, so a brand-new empty gallery greeted the client with a photograph
     of a stranger's horse as though it were theirs.

     The borrowed cover in case 2 is computed here and deliberately NOT saved.
     Saving it would make it indistinguishable from a chosen one, and case 1
     would then lock in a picture nobody actually picked. It also skips videos,
     which cannot render in an <img>. */
  /* How long this gallery stays up. Read from the retention record the
     photographer starts; null when no clock is running, which is the normal
     state for a gallery that has only just been uploaded. */
  const [retention, setRetention] = useState(null);

  useEffect(() => {
    if (!targetUid) { setRetention(null); return undefined; }
    let alive = true;
    fetchRetention(targetUid)
      .then((r) => { if (alive) setRetention(r); })
      .catch((e) => console.warn("Could not load retention:", e?.code || e));
    return () => { alive = false; };
  }, [targetUid]);

  /* The gallery split by competition, newest work first and the unfiled
     photos last. One group when nothing was ever filed, which renders
     identically to the way this page always looked. */
  const albumGroups = useMemo(() => {
    const groups = new Map();
    for (const item of mediaItems) {
      const key = item.album || "";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    return [...groups.entries()]
      .map(([album, items]) => ({ album, items }))
      .sort((a, b) => (a.album ? 0 : 1) - (b.album ? 0 : 1));
  }, [mediaItems]);

  /* The open lightbox follows its own photo: one tap can be on a competition
     that is paid for and the next on one that is not. */
  const modalLocked = isLocked(selectedItem?.album);

  /* True only when not a single photo may be taken — the notice at the top of
     the page says "these are previews until payment", and saying that over a
     gallery where one competition is already open would be a lie. */
  const nothingUnlocked = mediaItems.length > 0 && mediaItems.every((m) => isLocked(m.album));

  const autoCover = useMemo(() => {
    const firstPhoto = mediaItems.find((m) => !m.isVideo);
    return firstPhoto?.gridUrl || firstPhoto?.url || null;
  }, [mediaItems]);
  const coverSrc = userData?.coverImage || autoCover;

  // Admin editing of the displayed gallery's title + cover image
  const [ownerEmail, setOwnerEmail] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingCover, setSavingCover] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  const fetchMedia = async (u) => {
    if (!u) {
      setMediaItems([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Prefer the canonical folderKey stored on the user's profile; fall back
      // to guessing sanitized-email variants for older accounts.
      let folderCandidates = getUserFolderCandidates(u);
      try {
        const profileSnap = await getDoc(doc(db, "users", u.uid));
        const storedKey = profileSnap.exists() ? profileSnap.data().folderKey : "";
        if (storedKey) folderCandidates = [storedKey, ...folderCandidates.filter(k => k !== storedKey)];
      } catch (e) {
        console.warn("Could not read folderKey from profile:", e?.message);
      }
      console.log("Trying folders:", folderCandidates);

      const allFileRefsMap = new Map(); // key = fullPath
      /* Which candidate folder each file was found under. The album is the
         part of the path between that folder and the filename, and this
         gallery searches several spellings of a client's folder — so the root
         has to be remembered per file rather than assumed. */
      const rootOf = new Map();

      for (const path of folderCandidates) {
        try {
          const folderRef = ref(storage, path);
          const files = await listAllRecursive(folderRef);
          files.forEach((f) => {
            if (!allFileRefsMap.has(f.fullPath)) {
              allFileRefsMap.set(f.fullPath, f);
              rootOf.set(f.fullPath, path);
            }
          });
        } catch (e) {
          console.warn("Folder not found or error:", path, e?.message);
        }
      }

      const allItemRefs = Array.from(allFileRefsMap.values());

      const baseItemsPromises = allItemRefs.map(async (itemRef) => {
        if (itemRef.name === ".placeholder") return null;

        const fullUrl = await getDownloadURL(itemRef);
        const type = itemRef.name.split(".").pop();
        const isVid = isVideoExt((type || "").toLowerCase()) || isVideoUrl(fullUrl);
        // variant יוגדר ב-applyPattern לפי מיקום (wide/half), לא לפי גודל התמונה
        // כך נחסכת טעינה כפולה של כל תמונה רק לצורך מדידת מימדים
        const variant = "wide";

        // בפרודקשן (Vercel) נשתמש ב-/api/image שמקטין את התמונה
        // בדב (localhost) נשתמש ישירות ב-URL המקורי
        const isProduction = window.location.hostname !== "localhost" &&
          !window.location.hostname.startsWith("127.");
        const sized = (w, q) => (!isVid && isProduction)
          ? `/api/image?url=${encodeURIComponent(fullUrl)}&w=${w}&q=${q}`
          : fullUrl;

        const thumbUrl = sized(640, 70);   // אריח קטן בגריד
        const gridUrl  = sized(1280, 72);  // אריח רחב
        const modalUrl = sized(1600, 80);  // תצוגה מוגדלת — לא הקובץ המקורי הכבד
        /* What a locked gallery shows. Small and hard-compressed on purpose:
           this is the file a screenshot or a long-press-save would capture,
           so it has to be useless at any size that matters. Computed for
           every item regardless of lock state — it is a string, and only the
           one actually rendered is ever fetched. */
        const lockedUrl = sized(260, 28);

        return {
          id: itemRef.fullPath,
          url: fullUrl,      // מקורי — להורדה בלבד
          thumbUrl,
          gridUrl,
          modalUrl,
          lockedUrl,
          name: itemRef.name,
          type,
          isVideo: isVid,
          variant,
          lqipUrl: thumbUrl,
          // The competition this photo was filed under, "" for anything
          // uploaded before albums existed.
          album: albumOf(itemRef.fullPath, rootOf.get(itemRef.fullPath) || ""),
        };
      });

      const mediaData = (await Promise.all(baseItemsPromises)).filter(Boolean);
      setMediaItems(mediaData);
    } catch (e) {
      console.error(e);
      setError(t("me.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      let email = user.email;
      try {
        if (targetUid) {
          const snap = await getDoc(doc(db, "users", targetUid));
          if (!cancelled && snap.exists()) {
            const data = snap.data();
            setUserData(data);
            setTitleDraft(data.name || "");
            if (data.email) email = data.email;
          }
        }
      } catch (e) {
        console.warn("Could not load user data:", e);
      }
      if (!cancelled) setOwnerEmail(email);
      await fetchMedia({ uid: targetUid, email });
    })();

    return () => {
      cancelled = true;
    };
    // fetchMedia is re-created on every render; listing it here would refetch
    // the whole gallery on each one. This must run only when the viewer or the
    // gallery being viewed actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, targetUid]);

  // Records a download under the gallery's OWNER (not the viewer), so an admin
  // downloading a client's photos still shows up in that client's history.
  const recordDownload = (item) => {
    if (!targetUid && !ownerEmail) return;
    logDownload({ user: { uid: targetUid, email: ownerEmail }, item, ownerEmail });
  };

  // Admin: save an edited gallery title (stored on the user's profile as `name`)
  const saveTitle = async () => {
    if (!targetUid) return;
    const name = titleDraft.trim();
    try {
      await updateDoc(doc(db, "users", targetUid), { name });
      setUserData((d) => ({ ...(d || {}), name }));
      setEditingTitle(false);
    } catch (e) {
      alert(t("me.titleSaveFailed", { detail: e.message }));
    }
  };

  // Admin: upload a new cover image. Stored under covers/<uid> so it never
  // shows up inside the user's photo gallery.
  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !targetUid) return;
    setSavingCover(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const coverRef = ref(storage, `covers/${targetUid}.${ext}`);
      await uploadBytes(coverRef, file, {
        contentType: file.type || "image/jpeg",
        cacheControl: "public, max-age=3600",
      });
      const url = await getDownloadURL(coverRef);
      await updateDoc(doc(db, "users", targetUid), { coverImage: url });
      setUserData((d) => ({ ...(d || {}), coverImage: url }));
    } catch (err) {
      alert(t("me.coverUploadFailed", { detail: err.message }));
    } finally {
      setSavingCover(false);
      if (e.target) e.target.value = "";
    }
  };

  const filteredMediaItems = useMemo(
    () =>
      mediaItems.filter((item) => {
        const ext = (item.type || "").toLowerCase();
        if (filter === "images") return isImageExt(ext);
        if (filter === "videos") return isVideoExt(ext);
        return true;
      }),
    [mediaItems, filter]
  );

  const patternedItems = useMemo(() => {
    let out = [];
    let i = 0;
    while (i < filteredMediaItems.length) {
      if (i % 3 === 0) {
        out.push({ ...filteredMediaItems[i], variant: "wide" });
        i++;
      } else {
        out.push({ ...filteredMediaItems[i], variant: "half" });
        if (i + 1 < filteredMediaItems.length) {
          out.push({ ...filteredMediaItems[i + 1], variant: "half" });
        }
        i += 2;
      }
    }
    return out;
  }, [filteredMediaItems]);

  // eslint-disable-next-line no-unused-vars
  const totalPages = Math.ceil(patternedItems.length / itemsPerPage) || 1;
  // eslint-disable-next-line no-unused-vars
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return patternedItems.slice(start, start + itemsPerPage);
  }, [patternedItems, currentPage, itemsPerPage]);

  const openModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };
  const closeModal = () => {
    setSelectedItem(null);
    setModalOpen(false);
  };

  // Handler functions for new layout
  const handleSelectAll = () => {
    /* Only what can actually be downloaded. Selecting a locked competition
       would put a tick on a photo that the next button cannot deliver, and
       "select all → download" would come back short with no explanation. */
    const takeable = mediaItems.filter((item) => !isLocked(item.album));
    setSelectedItems(
      selectedItems.length === takeable.length ? [] : takeable.map((item) => item.id)
    );
  };

  /* One zip, not one download per photo.
     The old loop fired a separate browser download for every file with a
     350ms pause between them: fine for three photos, and for a hundred it is
     a "this site wants to download multiple files" prompt followed by half a
     minute of drip-feeding into the Downloads folder. A single file is also
     what somebody actually wants to keep.
     A lone photo still goes down as itself — wrapping one picture in an
     archive the client then has to unpack is worse than the problem. */
  const handleDownloadSelected = async () => {
    const itemsToDownload = mediaItems.filter(
      (item) => selectedItems.includes(item.id) && !isLocked(item.album)
    );
    if (itemsToDownload.length === 0) return;

    setInstallingAll(true);
    setZipProgress(null);
    setDownloadNote("");
    setShareQueue(null);
    try {
      if (itemsToDownload.length === 1) {
        await nativeDownload(itemsToDownload[0].url, itemsToDownload[0].name, { prefer: "auto" });
        recordDownload(itemsToDownload[0]);
        return;
      }

      /* ── ON A PHONE: the system share sheet, not a file ──────────────────
         A zip is the wrong answer on an iPhone. It lands in Files rather than
         Photos, it has to be unpacked, and every picture still has to be
         saved by hand afterwards — which is the chore this was meant to end.
         What the client actually wants is their photographs in the camera
         roll, and iOS already has a one-tap way to do that: a share sheet
         holding the files, with "Save 12 Images" on it.
         Fetch now, share on the next tap — Safari will not open a sheet from
         a gesture that has already spent ten seconds downloading. */
      if (shouldOfferShare(itemsToDownload.length)) {
        const { files, failed } = await fetchAsFiles(itemsToDownload, {
          onProgress: (p) => setZipProgress(p),
        });
        if (files.length > 0) {
          itemsToDownload.forEach((item) => recordDownload(item));
          setShareQueue({ groups: batches(files), index: 0 });
          if (failed.length > 0) setDownloadNote(t("me.zipPartial", { count: failed.length }));
          return;
        }
        // Nothing fetched — the bucket refused the bytes. Fall through to the
        // paths below, which end in per-file downloads that need no such
        // permission.
      }

      const folderName = (userData?.name || "lens-dance").trim().replace(/[\\/:*?"<>|]/g, "-");
      const { failed } = await downloadAsZip(itemsToDownload, {
        baseName: folderName,
        onProgress: (p) => setZipProgress(p),
        // Logged per photo, as before: the admin's download history is a list
        // of pictures, and collapsing it into "downloaded a zip" would lose
        // the only record of which ones a client actually took.
        onItemDone: (item) => recordDownload(item),
      });

      /* Nothing came through at all.
         That is what a bucket without a CORS policy looks like from here: the
         pictures display perfectly (an <img> needs no permission) while
         fetch() is refused the bytes, so the archive would be empty. Rather
         than tell the client their photos failed, fall back to the way this
         worked before — one download per file. Slower and noisier, but it
         does not need CORS, and a working download beats a tidy one.
         Fixing it properly is one command on the bucket; see the note in
         src/lib/zipDownload.js. */
      if (failed.length === itemsToDownload.length) {
        console.warn("Zip produced nothing — falling back to per-file downloads.");
        setZipProgress({ done: 0, total: itemsToDownload.length, phase: "fetching" });
        for (const item of itemsToDownload) {
          // eslint-disable-next-line no-await-in-loop
          await nativeDownload(item.url, item.name, { prefer: "auto" });
          recordDownload(item);
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 300));
        }
      } else if (failed.length > 0) {
        setDownloadNote(t("me.zipPartial", { count: failed.length }));
      }
    } catch (err) {
      setDownloadNote(t("common.errorWithCode", { detail: err?.message || "unknown" }));
    } finally {
      setInstallingAll(false);
      setZipProgress(null);
    }
  };

  /* The second tap. Opens the OS sheet for the current batch and advances.
     A cancelled sheet stops the queue rather than pushing the next batch at
     somebody who just dismissed one — but it keeps what is left, so they can
     pick up where they stopped. */
  const handleShareNext = async () => {
    if (!shareQueue) return;
    const group = shareQueue.groups[shareQueue.index];
    if (!group) { setShareQueue(null); return; }

    const result = await shareBatch(group, { title: userData?.name || "Lens Dance" });
    if (result === "failed") {
      setDownloadNote(t("me.shareFailed"));
      return;
    }
    if (result === "cancelled") return;

    const next = shareQueue.index + 1;
    if (next >= shareQueue.groups.length) setShareQueue(null);
    else setShareQueue({ ...shareQueue, index: next });
  };

  const handleToggleSelect = (item) => {
    setSelectedItems(prev => 
      prev.includes(item.id) 
        ? prev.filter(id => id !== item.id)
        : [...prev, item.id]
    );
  };

  const handleOpenModal = (item) => {
    openModal(item);
  };

  useEffect(() => {
    if (!modalOpen) return;
    const onKeyDown = (e) => e.key === "Escape" && closeModal();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [modalOpen]);

  if (authLoading || loading) {
    return (
      <div className="container" style={{ textAlign: "center" }}>
        <p>{authLoading ? t("me.checkingAuth") : t("me.loading")}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <main className="container" style={{ textAlign: "center" }}>
        <h2 className="section-title">{t("me.title")}</h2>
        <p>{t("me.notSignedIn")}</p>
        <Link className="auth-primary" to="/login">
          {t("me.signInPrompt")}
        </Link>
      </main>
    );
  }

  if (error) {
    return (
      <main className="container" style={{ textAlign: "center" }}>
        <h2 className="section-title">{t("me.title")}</h2>
        <p>{error}</p>
      </main>
    );
  }

  return (
    <div style={{ background: "#F5F1EA", minHeight: "100vh", direction: i18n.dir() }}>

      {/* ══════════════════════════════════════
          COVER IMAGE
      ══════════════════════════════════════ */}
      <div style={{
        position: "relative",
        height: coverSrc ? 380 : 260,
        overflow: "hidden", background: "#1A1208",
      }}>

        {/* The cover, in three states — see coverSrc above.
            It used to fall back to /pics/pic1.webp, a stock shot of somebody
            else's horse, which made an empty gallery look like it already had
            someone's photos in it. An empty gallery now shows no picture at
            all: just the dark band with the client's name on it. */}
        {coverSrc && (
          <img
            src={coverSrc}
            alt={t("me.coverAlt")}
            fetchpriority="high"
            decoding="async"
            style={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center 25%",
              display: "block",
            }}
          />
        )}

        {/* Gradient overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(20,10,4,.88) 0%, rgba(0,0,0,.1) 55%, transparent 100%)",
        }} />

        {/* Admin: change cover image */}
        {isAdmin && (
          <label style={{
            position: "absolute", top: 14, left: 14, zIndex: 5,
            fontFamily: "Arial, sans-serif", fontSize: 9,
            letterSpacing: ".16em", textTransform: "uppercase",
            color: "#fff", background: "rgba(0,0,0,.45)",
            border: "1px solid rgba(255,255,255,.5)",
            padding: "7px 14px", cursor: savingCover ? "wait" : "pointer",
            backdropFilter: "blur(2px)",
          }}>
            {savingCover ? t("me.uploading") : t("me.changeCover")}
            <input type="file" accept="image/*" onChange={onPickCover}
              disabled={savingCover} style={{ display: "none" }} />
          </label>
        )}

        {/* Centered bottom content */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", padding: "0 36px 32px",
          textAlign: "center",
        }}>

          {/* Eyebrow */}
          <span style={{
            fontFamily: "Arial, sans-serif", fontSize: 9,
            letterSpacing: ".24em", textTransform: "uppercase",
            color: "rgba(255,255,255,.45)", marginBottom: 10,
          }}>
            {t("me.personalGallery", { year: new Date().getFullYear() })}
          </span>

          {/* Rider name — editable by admin */}
          {isAdmin && editingTitle ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "center", margin: "0 0 8px" }}>
              <input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                autoFocus
                style={{
                  fontFamily: "Georgia, serif", fontSize: 26, color: "#fff",
                  background: "transparent", border: "none",
                  borderBottom: "2px solid rgba(255,255,255,.6)",
                  textAlign: "center", outline: "none", minWidth: 220,
                }}
              />
              <button onClick={saveTitle} style={{
                fontFamily: "Arial, sans-serif", fontSize: 9, letterSpacing: ".14em",
                textTransform: "uppercase", color: "#2C1E12", background: "#fff",
                border: "none", padding: "7px 14px", cursor: "pointer",
              }}>{t("common.save")}</button>
              <button onClick={() => { setEditingTitle(false); setTitleDraft(userData?.name || ""); }} style={{
                fontFamily: "Arial, sans-serif", fontSize: 9, letterSpacing: ".14em",
                textTransform: "uppercase", color: "rgba(255,255,255,.7)",
                background: "transparent", border: "none", cursor: "pointer",
              }}>{t("common.cancel")}</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 8px" }}>
              <h1 style={{
                fontFamily: "Georgia, serif", fontSize: 30,
                fontWeight: 400, color: "#fff",
                letterSpacing: ".04em", margin: 0,
              }}>
                {userData?.name || user?.displayName || t("me.yourGallery")}
              </h1>
              {isAdmin && (
                <button
                  onClick={() => { setEditingTitle(true); setTitleDraft(userData?.name || ""); }}
                  title={t("me.editTitle")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,.85)", fontSize: 15 }}
                >✏️</button>
              )}
            </div>
          )}

          {/* Nudge users without a full name to set one (helps the admin match photos) */}
          {!isAdmin && user && !(userData?.name || user?.displayName) && (
            <Link to="/change-name" style={{
              fontFamily: "Arial, sans-serif", fontSize: 11, letterSpacing: ".08em",
              color: "#FDFAF5", background: "rgba(178,150,125,.85)",
              padding: "8px 16px", textDecoration: "none", marginBottom: 14, display: "inline-block",
            }}>
              {t("me.addNameHint")}
            </Link>
          )}

          {/* Current horse name with decorative lines */}
          {userData?.horses?.length > 0 && (
            <div style={{
              display: "flex", alignItems: "center",
              gap: 12, marginBottom: 20,
            }}>
              <div style={{ height: 1, width: 32, background: "rgba(255,255,255,.3)" }} />
              <span style={{
                fontFamily: "Arial, sans-serif", fontSize: 12,
                letterSpacing: ".14em", color: "rgba(255,255,255,.72)",
              }}>
                {userData.horses[activeHorse]?.name || ""}
              </span>
              <div style={{ height: 1, width: 32, background: "rgba(255,255,255,.3)" }} />
            </div>
          )}

          {/* Horse switcher tabs — only if multiple horses */}
          {userData?.horses?.length > 1 && (
            <div style={{ display: "flex", gap: 8 }}>
              {userData.horses.map((horse, i) => (
                <button
                  key={i}
                  onClick={() => setActiveHorse(i)}
                  style={{
                    fontFamily: "Arial, sans-serif",
                    fontSize: 9, letterSpacing: ".18em",
                    textTransform: "uppercase",
                    padding: "8px 22px", cursor: "pointer",
                    border: `1px solid rgba(255,255,255,${activeHorse === i ? .72 : .3})`,
                    background: activeHorse === i ? "rgba(255,255,255,.15)" : "transparent",
                    color: `rgba(255,255,255,${activeHorse === i ? 1 : .5})`,
                    transition: "all .2s",
                  }}
                >
                  {horse.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        background: "#EDE8DF",
        borderBottom: "1px solid #DDD8CF",
      }}>
        {[
          ["📸", mediaItems.filter(m => !m.isVideo).length, t("me.photos")],
          ["🎬", mediaItems.filter(m => m.isVideo).length, t("me.videos")],
          ["📅", userData?.lastShootDate || "—", t("me.date")],
        ].map(([icon, val, label], i) => (
          <div key={i} style={{
            padding: "13px 16px", textAlign: "center",
            borderLeft: i > 0 ? "1px solid #DDD8CF" : "none",
          }}>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#2C1E12", marginBottom: 2 }}>
              {icon} {val}
            </div>
            <span style={{ fontFamily: "Arial, sans-serif", fontSize: 9, letterSpacing: ".1em", color: "#B2967D" }}>
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════
          HOW LONG THESE PHOTOS STAY UP
          Only shown once the photographer has started the clock. Sitting
          directly above the download toolbar on purpose: the moment a deadline
          becomes relevant is the moment somebody is deciding whether to
          download now or "later".
      ══════════════════════════════════════ */}
      {(() => {
        if (!retention?.expiresAt || mediaItems.length === 0) return null;
        const left = daysLeft(retention.expiresAt);
        const ends = toDate(retention.expiresAt);
        const soon = left !== null && left <= 7;
        const gone = left !== null && left <= 0;
        return (
          <div style={{
            padding: "11px 28px",
            background: gone ? "#FFF0EE" : soon ? "#FFF8E8" : "#F7FBF1",
            borderBottom: `1px solid ${gone ? "#E8C4BC" : soon ? "#EBD9A8" : "#D8E8C0"}`,
            fontFamily: "Arial, sans-serif", fontSize: 11.5, lineHeight: 1.7,
            color: gone ? "#8A2A1F" : soon ? "#7A5A00" : "#3B6D11",
            textAlign: "center", direction: i18n.dir(),
          }}>
            {gone
              ? t("me.retentionExpired")
              : t("me.retentionNotice", {
                  count: left,
                  date: ends ? ends.toLocaleDateString(i18n.language) : "",
                })}
          </div>
        );
      })()}

      <div style={{
        padding: "13px 28px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #E2D9CE",
        background: "#FDFAF5",
      }}>
        <span style={{ fontFamily: "Arial, sans-serif", fontSize: 10, letterSpacing: ".08em", color: "#9A8878" }}>
          {t("me.countLine", { count: mediaItems.length })}
        </span>
        {/* Selecting and downloading exist only once the gallery is released.
            Disabling them would be worse than removing them: a button that
            refuses is read as a fault in the site, while their absence next to
            the notice below reads as "not yet". */}
        <div style={{ display: "flex", gap: 10 }}>
          {/* The toolbar speaks for the whole page, so it only steps aside
             when nothing at all can be taken. With one competition paid for
             and another not, selecting and downloading stay available and
             each photo answers for itself. */}
          {nothingUnlocked ? (
            /* A sentence, not a label: "not released yet" told the client a
               state without telling them what it meant for the pictures in
               front of them. Left in normal case — uppercase letter-spacing
               makes a full sentence hard to read at this size. */
            <span style={{ fontFamily: "Arial, sans-serif", fontSize: 10.5, color: "#B2967D", lineHeight: 1.6 }}>
              {t("me.lockedTag")}
            </span>
          ) : (
          <>
          {/* Select all button */}
          <button
            onClick={handleSelectAll}
            style={{
              fontFamily: "Arial, sans-serif", fontSize: 9,
              letterSpacing: ".14em", textTransform: "uppercase",
              color: "#B2967D", background: "transparent", border: "none",
              cursor: "pointer", borderBottom: "1px solid #B2967D", paddingBottom: 1,
            }}
          >
            {t("me.selectAll")}
          </button>
          {/* Download button */}
          {/* Says where it has got to. Building a zip of a whole competition
              takes a while with nothing to see — an unchanged button is read
              as a button that did not work, and gets pressed again. */}
          <button
            onClick={handleDownloadSelected}
            disabled={installingAll}
            style={{
              fontFamily: "Arial, sans-serif", fontSize: 9,
              letterSpacing: ".14em", textTransform: "uppercase",
              color: "#4A3525", border: "1px solid #4A3525",
              background: "transparent", padding: "6px 16px",
              cursor: installingAll ? "wait" : "pointer",
              opacity: installingAll ? 0.65 : 1,
            }}
          >
            {!installingAll
              ? t("me.downloadSelected")
              : zipProgress?.phase === "packing"
                ? t("me.zipPacking")
                : t("me.zipProgress", {
                    done: zipProgress?.done ?? 0,
                    total: zipProgress?.total ?? selectedItems.length,
                  })}
          </button>
          </>
          )}
        </div>
      </div>

      {/* Ready to go into the phone. Deliberately loud and deliberately the
          only thing to press: the photos are already downloaded at this point
          and one tap away from the camera roll, and a client who misses this
          step has waited for nothing. */}
      {shareQueue && (
        <div style={{
          background: "#F7FBF1", borderBottom: "1px solid #C0DD97",
          padding: "16px 22px", textAlign: "center",
        }}>
          <button
            type="button"
            onClick={handleShareNext}
            style={{
              fontFamily: "Arial, sans-serif", fontSize: 11, letterSpacing: ".16em",
              textTransform: "uppercase", background: "#3B6D11", color: "#FFF",
              border: "none", padding: "13px 30px", cursor: "pointer", minHeight: 46,
            }}
          >
            {t("me.saveToPhone", { count: shareQueue.groups[shareQueue.index]?.length || 0 })}
          </button>
          <p style={{ fontFamily: "Arial, sans-serif", fontSize: 10.5, color: "#5A7A3A", margin: "9px 0 0", lineHeight: 1.7 }}>
            {shareQueue.groups.length > 1
              ? t("me.saveBatch", { index: shareQueue.index + 1, total: shareQueue.groups.length })
              : t("me.saveHint")}
          </p>
        </div>
      )}

      {downloadNote && (
        <div style={{
          background: "#FFF6F4", borderBottom: "1px solid #E8C4BC",
          padding: "10px 22px", textAlign: "center",
          fontFamily: "Arial, sans-serif", fontSize: 11, color: "#8A2A1F",
        }}>
          {downloadNote}
        </div>
      )}

      {/* Why the photos look the way they do. Without this the client reads a
          soft, watermarked gallery as a broken one and writes to ask what
          happened — which is the message this paragraph replaces. */}
      {nothingUnlocked && (
        <div style={{
          background: "#FDF6E9", borderBottom: "1px solid #E8DCC4",
          padding: "14px 22px", textAlign: "center",
        }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#4A3525", margin: "0 0 4px" }}>
            {t("me.lockedTitle")}
          </p>
          <p style={{ fontFamily: "Arial, sans-serif", fontSize: 11, lineHeight: 1.8, color: "#8A7868", margin: 0 }}>
            {t("me.lockedBody")}
          </p>
        </div>
      )}

      {/* ══════════════════════════════════════
          PHOTO / VIDEO GRID — masonry
          Same layout as the public gallery: every frame is shown WHOLE. Tiles
          share a column width and take their height from the media itself, so
          a vertical clip stands tall next to a landscape still and neither is
          cropped. Column count lives in style.css (.gallery-masonry).
      ══════════════════════════════════════ */}
      <div style={{ padding: "16px 22px 40px", background: "#FAFAF8" }}>
        {albumGroups.map((group) => (
        <React.Fragment key={group.album || "__all"}>
        {/* A heading per competition, and only when there is more than one.
            A client photographed three times was getting one wall of two
            hundred pictures with no way to tell last month from last year;
            a client photographed once should not be made to read a heading
            that states the obvious. Photos uploaded before albums existed sit
            under "the rest", last, because they are the oldest. */}
        {albumGroups.length > 1 && (
          <div style={{ margin: "10px 2px 14px" }}>
            <span style={{
              fontFamily: "Georgia, serif", fontSize: 17, color: "#2C1E12",
            }}>
              {group.album || t("me.albumOther")}
            </span>
            {isLocked(group.album) && (
              <span style={{
                fontFamily: "Arial, sans-serif", fontSize: 9.5, letterSpacing: ".06em",
                color: "#8A6A22", background: "#FDF6E9", border: "1px solid #E8DCC4",
                padding: "3px 9px", marginInlineStart: 10,
              }}>
                {t("me.albumLocked")}
              </span>
            )}
            <span style={{
              fontFamily: "Arial, sans-serif", fontSize: 10, color: "#B2967D",
              marginInlineStart: 10, letterSpacing: ".1em",
            }}>
              {t("me.countLine", { count: group.items.length })}
            </span>
            <div style={{ height: 1, background: "#E2D9CE", marginTop: 8 }} />
          </div>
        )}
        <div className="gallery-masonry">
          {group.items.map((item, index) => {
            const isVideo = item.isVideo;
            /* Per photo, because the competition it belongs to may be paid
               for while the next one is not. */
            const tileLocked = isLocked(item.album);
            const isLoaded = loaded[item.id];
            const isSelected = selectedItems?.includes(item.id);
            const isLiked = likedPaths.has(item.id);

            return (
              <div
                key={item.id || index}
                className={`gallery-tile${isLoaded ? " is-loaded" : ""}${tileLocked ? " is-locked-tile" : ""}`}
                onClick={() => handleOpenModal(item)}
                /* Long-press-to-save and right-click-save are the casual way
                   round a locked gallery, so they are closed. Not a serious
                   barrier — anyone who opens devtools still gets the preview —
                   but the preview is all there is to get. */
                onContextMenu={tileLocked ? (e) => e.preventDefault() : undefined}
                onDragStart={tileLocked ? (e) => e.preventDefault() : undefined}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleOpenModal(item)}
              >
                {isVideo ? (
                  <>
                    <video
                      src={item.gridUrl || item.url}
                      className="gallery-tile-media"
                      muted playsInline preload="metadata"
                      onLoadedMetadata={() => setLoaded((l) => ({ ...l, [item.id]: true }))}
                    />
                    <span className="gallery-tile-play" aria-hidden="true" />
                  </>
                ) : (
                  <img
                    src={tileLocked
                      ? (item.lockedUrl || item.url)
                      : (item.thumbUrl || item.gridUrl || item.url)}
                    alt=""
                    className={`gallery-tile-media${tileLocked ? " is-locked-media" : ""}`}
                    draggable={!tileLocked}
                    loading={index < 4 ? "eager" : "lazy"}
                    fetchpriority={index < 2 ? "high" : undefined}
                    decoding="async"
                    onLoad={(e) => {
                      /* Lock the tile to the real ratio as soon as it is known,
                         so the columns stop reflowing as later photos arrive. */
                      const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                      if (w && h) e.currentTarget.parentElement.style.aspectRatio = `${w} / ${h}`;
                      setLoaded((l) => ({ ...l, [item.id]: true }));
                    }}
                  />
                )}

                {/* Favourite. Top-left, opposite the selection circle.
                    On a desktop it fades in when the tile is hovered; on a
                    touch screen there is no hover, so .gallery-like is always
                    visible there (see style.css). A heart that is already set
                    stays visible either way — otherwise you could not tell
                    what you had marked without hovering every photo.
                    Read-only when the admin is looking at a client's gallery. */}
                {(canLike || isLiked) && (
                  <button
                    type="button"
                    className={`gallery-like${isLiked ? " is-liked" : ""}${canLike ? "" : " is-readonly"}`}
                    aria-label={isLiked ? t("me.unlike") : t("me.like")}
                    aria-pressed={isLiked}
                    title={isLiked ? t("me.unlike") : t("me.like")}
                    disabled={!canLike}
                    onClick={(e) => { e.stopPropagation(); handleToggleLike(item); }}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                      <path
                        d="M12 20.7 4.6 13.3a4.6 4.6 0 0 1 6.5-6.5l.9.9.9-.9a4.6 4.6 0 0 1 6.5 6.5Z"
                        fill={isLiked ? "currentColor" : "none"}
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}

                {/* Selection checkbox — sits above the media, and stops the
                    click from also opening the lightbox. */}
                <div
                  hidden={tileLocked}
                  onClick={e => { e.stopPropagation(); handleToggleSelect(item); }}
                  style={{
                    position: "absolute", top: 8, right: 8, zIndex: 2,
                    width: 20, height: 20, borderRadius: "50%",
                    border: `1.5px solid rgba(255,255,255,${isSelected ? 0 : .6})`,
                    background: isSelected ? "#B2967D" : "rgba(0,0,0,.2)",
                    cursor: "pointer", transition: "all .15s",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {isSelected && (
                    <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </React.Fragment>
        ))}

        {/* Watermark */}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <span style={{ fontFamily: "Arial, sans-serif", fontSize: 8, letterSpacing: ".12em", color: "#C0B0A0" }}>
            Lens Dance Photography · lens-dance.com
          </span>
        </div>
      </div>

      {/* Keep existing modal JSX below — do not change it */}
      {modalOpen && selectedItem && (
        <div className="media-modal" onClick={closeModal} role="dialog" aria-modal="true">
          <div className="media-modal-content" onClick={(e) => e.stopPropagation()}>
            {isVideoUrl(selectedItem.url) ? (
              <video
                src={selectedItem.url}
                controls
                className="modal-media"
                preload="metadata"
                playsInline
                style={{ background: "#000", maxHeight: "80vh", width: "100%" }}
              />
            ) : (
              <img
                src={modalLocked
                  ? (selectedItem.lockedUrl || selectedItem.url)
                  : (selectedItem.modalUrl || selectedItem.url)}
                alt={selectedItem.name}
                className={`modal-media${modalLocked ? " is-locked-media" : ""}`}
                draggable={!modalLocked}
                onContextMenu={modalLocked ? (e) => e.preventDefault() : undefined}
                style={{
                  background: "#111",
                  maxHeight: "80vh",
                  width: "100%",
                  objectFit: "contain",
                }}
              />
            )}
            <div className="modal-actions">
              {modalLocked ? (
                <span style={{ fontFamily: "Arial, sans-serif", fontSize: 11, color: "#B2967D" }}>
                  {t("me.lockedTag")}
                </span>
              ) : (
                <button
                  type="button"
                  className="download-btn"
                  onClick={() => {
                    nativeDownload(selectedItem.url, selectedItem.name, { prefer: "auto" });
                    recordDownload(selectedItem);
                  }}
                >
                  {t("me.download")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}